import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowRight, ShieldCheck, ShoppingBag, Truck } from "lucide-react";
import { clearCart, useCart } from "@/lib/cart/store";
import { calcPricing, formatPrice } from "@/lib/cart/pricing";
import { useShippingConfig } from "@/lib/admin/settings";
import { CartLineRow } from "@/components/cart/CartLineRow";
import { CartSummary } from "@/components/cart/CartSummary";
import { PromoInput } from "@/components/cart/PromoInput";
import { CartPriceNotice } from "@/components/cart/CartPriceNotice";
import { useCartPriceSync } from "@/lib/cart/use-price-sync";
import { usePromoCode } from "@/lib/cart/use-promo";

export const Route = createFileRoute("/cart")({
  head: () => ({
    meta: [
      { title: "Your Bag — CB16 Maison" },
      {
        name: "description",
        content:
          "Review the fragrances in your CB16 Maison bag, apply a promo code, and proceed to checkout.",
      },
      { property: "og:title", content: "Your Bag — CB16 Maison" },
      {
        property: "og:description",
        content: "Review your CB16 Maison selection and proceed to checkout.",
      },
      { property: "og:type", content: "website" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: CartPage,
});

function CartPage() {
  const lines = useCart();
  const priceSync = useCartPriceSync(lines);
  const promo = usePromoCode(lines);
  const shippingConfig = useShippingConfig();
  const pricing = calcPricing(lines, promo.applied, null, shippingConfig);
  const isEmpty = lines.length === 0;

  return (
    <div className="bg-white">
      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-10 lg:py-16">
        <header className="mb-10 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#C6A15B]">Your Bag</p>
            <h1
              className="mt-2 text-3xl text-black sm:text-4xl"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Shopping Bag
            </h1>
          </div>
          {!isEmpty && (
            <span className="text-[11px] uppercase tracking-[0.22em] text-black/50">
              {lines.reduce((s, l) => s + l.quantity, 0)} item
              {lines.reduce((s, l) => s + l.quantity, 0) === 1 ? "" : "s"}
            </span>
          )}
        </header>

        {isEmpty ? (
          <EmptyCart />
        ) : (
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_360px]">
            <section aria-label="Cart items" className="min-w-0">
              <div className="divide-y divide-black/10 border-y border-black/10">
                {lines.map((line) => (
                  <CartLineRow
                    key={`${line.productId}:${line.variantId}`}
                    line={line}
                    variant="page"
                  />
                ))}
              </div>
              <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                <Link
                  to="/fragrances"
                  className="text-[11px] font-medium uppercase tracking-[0.22em] text-black/70 transition hover:text-[#C6A15B]"
                >
                  ← Continue Shopping
                </Link>
                <button
                  type="button"
                  onClick={() => clearCart()}
                  className="text-[11px] font-medium uppercase tracking-[0.22em] text-black/50 transition hover:text-black"
                >
                  Clear Bag
                </button>
              </div>
            </section>

            <aside aria-label="Order summary" className="lg:sticky lg:top-24 lg:self-start">
              <div className="border border-black/10 bg-white p-6">
                <h2
                  className="mb-4 text-lg text-black"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Order Summary
                </h2>
                <CartSummary pricing={pricing} />
                <CartPriceNotice sync={priceSync} />
                <div className="mt-6">
                  <PromoInput promo={promo} discount={pricing.discount} />
                </div>
                <Link
                  to="/checkout"
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 border border-black bg-black px-6 py-3.5 text-[11px] font-medium uppercase tracking-[0.22em] text-white transition hover:bg-[#C6A15B] hover:border-[#C6A15B]"
                >
                  Proceed to Checkout
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <ul className="mt-6 space-y-2 text-[11px] uppercase tracking-[0.22em] text-black/50">
                  {pricing.freeShippingThreshold ? (
                    <li className="flex items-center gap-2">
                      <Truck className="h-3.5 w-3.5 text-[#C6A15B]" /> Free Standard Delivery over{" "}
                      {formatPrice(pricing.freeShippingThreshold)}
                    </li>
                  ) : null}
                  <li className="flex items-center gap-2">
                    <ShieldCheck className="h-3.5 w-3.5 text-[#C6A15B]" /> Secure encrypted checkout
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyCart() {
  return (
    <div className="flex flex-col items-center gap-5 border border-black/10 px-6 py-20 text-center">
      <ShoppingBag className="h-10 w-10 text-black/30" strokeWidth={1.25} />
      <h2 className="text-2xl text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
        Your bag is empty
      </h2>
      <p className="max-w-md text-sm text-black/60">
        Explore the CB16 Maison collection to begin composing your olfactive wardrobe. Every
        fragrance is hand-blended in small batches at the CB16 atelier in Mumbai, India.
      </p>
      <div className="mt-2 flex flex-wrap justify-center gap-3">
        <Link
          to="/fragrances"
          className="inline-flex items-center justify-center border border-black bg-black px-6 py-3 text-[11px] font-medium uppercase tracking-[0.22em] text-white transition hover:bg-[#C6A15B] hover:border-[#C6A15B]"
        >
          Shop Fragrances
        </Link>
        <Link
          to="/collections"
          className="inline-flex items-center justify-center border border-black/20 bg-white px-6 py-3 text-[11px] font-medium uppercase tracking-[0.22em] text-black transition hover:border-[#C6A15B] hover:text-[#C6A15B]"
        >
          Explore Collections
        </Link>
      </div>
    </div>
  );
}
